import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import { ToasterMessage } from '@fcom/common/interfaces';
import { snapshot } from '@fcom/rx';
import { CustomServiceType, SeatCharacteristics } from '@fcom/dapi';
import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairServiceRequestItem,
} from '@fcom/dapi/api/models';
import { NotificationTheme } from '@fcom/ui-components';
import { capitalizeWord } from '@fcom/core/utils';
import { BookingAppState, SelectionPhase } from '@fcom/common/interfaces/booking';
import { FlightSeatSelections } from '@fcom/common/interfaces/seat-map.interface';

import { seatSelectionsForFlight, serviceSelectionsForFragment } from '../store';
import { serviceSelectionToUpdateRequest } from '../modules/ancillaries/utils';
import { ItineraryModal } from '../modules/ancillaries/interfaces';

export interface AncillaryToasterMessages {
  error: ToasterMessage;
}

const getRequests = (
  store$: Store<BookingAppState>,
  category: Category,
  fragmentIds: string[]
): FinnairServiceRequestItem[] => {
  return fragmentIds.map((fragmentId) => ({
    category,
    fragmentId,
    serviceSelection: serviceSelectionToUpdateRequest(
      snapshot(store$.pipe(serviceSelectionsForFragment(fragmentId, category)))
    ),
  }));
};

const getMessage = (category: Category, key: string, theme: ToasterMessage['theme']): ToasterMessage => ({
  id: `ANCILLARY_${category.toString()}_${key}`.toUpperCase(),
  contentKey: `ancillaries.toaster.${key}`,
  theme,
});

const getToasterMessages = (category: Category, errorKey: string): AncillaryToasterMessages => {
  return {
    error: getMessage(category, errorKey, NotificationTheme.ALERT),
  };
};

export const mapToSeatRequest = (
  flightId: string,
  selectionsForFlight: FlightSeatSelections
): FinnairServiceRequestItem[] => {
  return [
    {
      category: Category.SEAT,
      fragmentId: flightId,
      serviceSelection: selectionsForFlight
        ? Object.keys(selectionsForFlight).map((travelerId) => ({
            travelerId,
            variant: selectionsForFlight[travelerId].seatType || '',
            seatNumber: selectionsForFlight[travelerId].seatNumber,
            isExitSeat: selectionsForFlight[travelerId].characteristics?.includes(SeatCharacteristics.EXIT) ?? false,
            quantity: selectionsForFlight[travelerId].seatNumber ? 1 : 0,
          }))
        : [],
    },
  ];
};

export const containsUnpaidSeats = (order: FinnairOrder): boolean => {
  return !!order.services.unpaid?.find((s) => s.category === Category.SEAT);
};

export const getServiceData = (
  store$: Store<BookingAppState>,
  category: Category,
  bounds: FinnairBoundItem[],
  flights: FinnairItineraryItemFlight[]
): [FinnairServiceRequestItem[], AncillaryToasterMessages] => {
  switch (category) {
    case Category.CABIN_BAGGAGE:
    case Category.BAGGAGE:
    case Category.SPORT:
    case Category.SAF:
    case Category.PET: {
      const keySuffix = category === Category.BAGGAGE ? 'Bags' : capitalizeWord(category);
      return [
        getRequests(
          store$,
          category,
          bounds.map((b) => b.id)
        ),
        getToasterMessages(category, 'errorSaving' + keySuffix),
      ];
    }
    case Category.SEAT: {
      const requests: FinnairServiceRequestItem[] = flights.reduce((all, flight) => {
        return all.concat(mapToSeatRequest(flight.id, snapshot(store$.select(seatSelectionsForFlight(flight.id)))));
      }, []);

      return [requests, getToasterMessages(category, 'errorSavingSeats')];
    }
    case Category.LOUNGE:
    case Category.WIFI:
    case Category.MEAL:
    case Category.PRIORITY: {
      const keySuffix = category === Category.MEAL ? 'Meals' : capitalizeWord(category);
      return [
        getRequests(
          store$,
          category,
          flights.map((b) => b.id)
        ),
        getToasterMessages(category, 'errorSaving' + keySuffix),
      ];
    }
    case Category.COVER: {
      return [
        getRequests(store$, category, [CustomServiceType.JOURNEY]),
        getToasterMessages(category, 'errorSavingTripCover'),
      ];
    }
    case Category.SEAT_WIFI:
    case Category.SEAT_MEAL:
    case Category.SEAT_CABIN_BAGGAGE:
    case Category.SEAT_PRIORITY:
      // Bundles are handled separately by the update-request selectors
      return [[], undefined];
    default:
      return [[], undefined];
  }
};

export const getBoundDetailsModalData = (
  cartOrOrder$: Observable<FinnairCart | FinnairOrder>,
  slideIndex$: Observable<number>
): ItineraryModal => {
  const bounds$ = cartOrOrder$.pipe(map((cart) => cart.bounds));

  return {
    open: true,
    data: {
      outbound$: bounds$.pipe(map((bounds) => bounds[0])),
      inbound$: bounds$.pipe(map((bounds) => (bounds.length > 1 ? bounds[1] : undefined))),
      locations$: cartOrOrder$.pipe(map((c) => c.locations)),
      selectionPhase$: slideIndex$.pipe(
        map((slideIndex) => (slideIndex === 0 ? SelectionPhase.OUTBOUND : SelectionPhase.INBOUND))
      ),
    },
  };
};
